import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { userReferralsStateAtom } from '@root/entities/referral'
import { Drawer, InfoMessage } from '@root/shared/ui-kit'

import { RefereeItem } from './RefereeItem'

import type { DrawerProps } from '@root/shared/ui-kit'

type Props = Pick<DrawerProps, 'onOpenChange' | 'open'>

export const ReferralListDrawer: React.FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation()
  const [referralsState] = useAtom(userReferralsStateAtom)
  const loadReferrals = useAction(userReferralsStateAtom.run)
  const referrals = referralsState.value ?? []

  const renderContent = () => {
    if (AsyncStateN.isError(referralsState)) {
      return (
        <Error>
          <InfoMessage intent="error" onRetry={() => loadReferrals()} />
        </Error>
      )
    }

    if (AsyncStateN.isSuccess(referralsState)) {
      return (
        <List data-vaul-no-drag>
          {referralsState.value.map((ref, idx) => {
            return (
              <RefereeItem key={ref.id} referee={ref} darkBg={idx % 2 === 0} />
            )
          })}
        </List>
      )
    }

    return (
      <LoadingContainer>
        {t('common.loading')}
        <CircularLoader size={18} />
      </LoadingContainer>
    )
  }

  return (
    <>
      <Root
        open={open}
        onOpenChange={onOpenChange}
        title={t('referral.your_referrals')}
      >
        <Content>
          <Header>
            <HeaderCell
              css={css`
                display: flex;
                gap: 4px;
                align-items: center;
              `}
            >
              <Count>{referrals.length}</Count>
              <span>{t('leaderboard.user_names')}</span>
            </HeaderCell>
            <HeaderCell>{t('common.date')}</HeaderCell>
            <HeaderCell
              css={css`
                text-align: right;
              `}
            >
              {t('common.profit')}
            </HeaderCell>
          </Header>
          {renderContent()}
        </Content>
      </Root>
    </>
  )
}

const Root = styled(Drawer)`
  height: var(--max-drawer-height);
`

const Content = styled.div`
  padding-block: 16px 40px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const Error = styled.div`
  display: grid;
  align-items: center;
`

const List = styled.div`
  overflow-y: auto;
  padding-block: 0 32px;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 50% auto 1fr;
  align-items: center;
  padding-inline: 12px;

  border-bottom: 1px solid rgba(255 255 255 / 5%);
  padding-block: 8px;
`

const HeaderCell = styled.div`
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  color: var(--text-9);
`

const Count = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: var(--text-12);
`

// TODO: Extract this component to UI-kit
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-size: 12px;
`
